import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
  Modal,
  useTheme,
  FormHelperText,
  useMediaQuery,
  TextField,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useGetTemplates } from '../../hooks/useGetTemplates';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { useSendTemplate } from '../../hooks/useSendTemplate';
import { useSendTemplateWithFile } from '../../hooks/useSendTemplateWithFile';
import { useWhatsappClient } from '../WhatsappClientContext';
import { LoadingButton } from '@mui/lab';
import { AttachFile } from '@mui/icons-material';

export function replacePlaceholders(template: string, values: string[]) {
  return template.replace(/{{(\d+)}}/g, (_, index) => {
    const i = parseInt(index, 10) - 1;
    return values[i] ?? '';
  });
}

const formSchema = z.object({
  templateId: z.string().nonempty('whatsapp.templateMessenger.selectTemplate'),
  placeholders: z.array(z.object({ value: z.string().nonempty('Campo requerido') })),
  file: z.any().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const TemplateMessenger = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useTranslation();
  const { clientData } = useWhatsappClient();
  const sendTemplate = useSendTemplate(clientData?.agencyId);
  const sendTemplateWithFile = useSendTemplateWithFile(clientData?.agencyId);
  const { data, isLoading } = useGetTemplates(clientData?.agencyId);
  const { palette, breakpoints } = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      templateId: '',
      placeholders: [],
      file: undefined,
    },
    mode: 'onChange',
  });

  const templateId = watch('templateId');
  const placeholdersValues = watch('placeholders');
  
  const [searchText, setSearchText] = useState('');

  const enabledTemplates = (data?.templates || [])
    .filter((template) => template.enabled !== false)
  const availableTemplates = enabledTemplates.filter((template) => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return true;
    const name = template.elementName || '';
    const id = template.id || '';
    return `${name} ${id}`.toLowerCase().includes(searchValue);
  });

  const selectedTemplate = enabledTemplates.find((t) => t.id === templateId);
  const templateOptions =
    selectedTemplate && !availableTemplates.some((t) => t.id === templateId)
      ? [selectedTemplate, ...availableTemplates]
      : availableTemplates;
  
  // Determinar si la plantilla requiere un archivo (tipo documento o imagen)
  const templateTypeLC = selectedTemplate?.templateType?.toLowerCase() || '';
  const isDocumentTemplate = templateTypeLC === 'document';
  const isImageTemplate = templateTypeLC === 'image';
  const requiresFile = isDocumentTemplate || isImageTemplate;

  const { fields, replace } = useFieldArray({
    control,
    name: 'placeholders',
  });

  useEffect(() => {
    if (!open) {
      reset({
        templateId: '',
        placeholders: [],
        file: undefined,
      });
      setSelectedFile(null);
      setFileError(null);
      setSearchText('');
    }
  }, [open, reset]);

  useEffect(() => {
    if (selectedTemplate) {
      const placeholderMatches = selectedTemplate.data.match(/{{\d+}}/g) || [];
      
      const newPlaceholders = placeholderMatches.map(() => ({ value: '' }));
      
      if (fields.length !== newPlaceholders.length) {
        replace(newPlaceholders);
      }
      
      // Reset file when template changes
      setSelectedFile(null);
      setFileError(null);
    } else if (fields.length > 0) {
      replace([]);
    }
  }, [selectedTemplate, replace, fields.length]);

  useEffect(() => {
    if (templateId && !selectedTemplate) {
      setValue('templateId', '');
    }
  }, [templateId, selectedTemplate, setValue]);

  const handleClose = () => {
    reset({
      templateId: '',
      placeholders: [],
      file: undefined,
    });
    setSelectedFile(null);
    setFileError(null);
    setSearchText('');
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setFileError(null);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!selectedTemplate) return;

    const { templateId, placeholders } = data;
    const placeholderValues = placeholders.map((p) => p.value);
    const finalText = replacePlaceholders(selectedTemplate.data, placeholderValues);

    if (!clientData?.whatsappNumber) return;
    
    // Check if file is required but not provided
    if (requiresFile && !selectedFile) {
      setFileError(t('whatsapp.templateMessenger.fileRequired'));
      return;
    }

    const onSuccess = () => {
      reset({
        templateId: '',
        placeholders: [],
        file: undefined,
      });
      setSelectedFile(null);
      setFileError(null);
      onClose();
    };

    if (requiresFile && selectedFile) {
      // Use sendTemplateWithFile for document or image templates
      sendTemplateWithFile.mutate(
        {
          file: selectedFile,
          clientId: clientData.id,
          templateName: selectedTemplate.elementName,
          languageCode: selectedTemplate.languageCode || 'es',
          bodyParameters: placeholderValues.length > 0 
            ? placeholderValues.map(value => ({ type: 'text', text: value })) 
            : undefined,
        },
        { onSuccess }
      );
    } else {
      // Use regular sendTemplate for text templates
      sendTemplate.mutate(
        {
          destination: clientData.whatsappNumber,
          templateId: templateId,
          templateParams: placeholderValues || [],
          messageText: finalText,
        },
        { onSuccess }
      );
    }
  };
  
  const isMobile = useMediaQuery(breakpoints.down('md'));

  if (isLoading && !isMobile) return <CircularProgress />;

  const canSubmit = () => {
    if (!templateId) return false;
    
    if (requiresFile && !selectedFile) return false;
    
    if (fields.length > 0) {
      return placeholdersValues?.every(p => p?.value.trim() !== '');
    }
    
    return true;
  };

  const previewText = selectedTemplate 
    ? replacePlaceholders(
        selectedTemplate.data,
        (placeholdersValues || []).map((p) => p?.value || '')
      )
    : '';

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 300,
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="h6" align="center" gutterBottom>
              {t('whatsapp.templateMessenger.title')}
            </Typography>

            <TextField
              label={t('common.search')}
              placeholder={t('common.search')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              fullWidth
              size="small"
            />
            
            <Controller
              name="templateId"
              control={control}
              render={({ field }) => (
                <Select 
                  {...field} 
                  displayEmpty 
                  fullWidth
                  error={!!errors.templateId}
                >
                  <MenuItem value="" disabled>
                    {t('whatsapp.templateMessenger.selectTemplate')}
                  </MenuItem>
                  {templateOptions.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      {tpl.elementName} {tpl.templateType ? `(${tpl.templateType.toLowerCase()})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            
            {selectedTemplate && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('whatsapp.templateMessenger.preview')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      p: 2, 
                      bgcolor: palette.paper.dark, 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {previewText}
                  </Typography>
                </Box>

                {requiresFile && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('whatsapp.templateMessenger.uploadFile')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        startIcon={<AttachFile />}
                      >
                        {t('common.selectFile')}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        accept={isImageTemplate ? 'image/*' : '*/*'}
                        style={{ display: 'none' }}
                      />
                      {selectedFile ? (
                        <Typography variant="body2" sx={{ ml: 1, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedFile.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('whatsapp.templateMessenger.noFileSelected')}
                        </Typography>
                      )}
                    </Box>
                    {fileError && (
                      <FormHelperText error>{fileError}</FormHelperText>
                    )}
                  </Box>
                )}

                {fields.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('whatsapp.templateMessenger.completeFields')}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {fields.map((field, idx) => (
                        <ControlledTextField
                          key={field.id}
                          control={control}
                          name={`placeholders.${idx}.value`}
                          placeholder={t('whatsapp.templateMessenger.placeholder', { number: idx + 1 })}
                          required
                          errorMessage={errors.placeholders?.[idx]?.value?.message}
                          fullWidth
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={handleClose} variant="outlined">
                {t('common.cancel')}
              </Button>
              <LoadingButton 
                variant="contained" 
                color="primary" 
                type="submit"
                disabled={!canSubmit()}
                loading={sendTemplate.isLoading || sendTemplateWithFile.isLoading}
              >
                {t('whatsapp.templateMessenger.sendMessage')}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
